import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Divider,
	IconButton,
	Skeleton,
	Stack,
	Tooltip,
	Typography,
	useTheme,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import LinkOutlined from "@mui/icons-material/LinkOutlined";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import { Document as PdfDocument, Page as PdfPage, pdfjs } from "react-pdf";
import * as XLSX from "xlsx";
import * as mammoth from "mammoth/mammoth.browser.js";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";

import ModalWrapper from "../ModalWrapper/ModalWrapper";
import { getDocumentAccessUrl } from "../../services/documentService";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const DEFAULT_ATTACHMENT = { type: "none", value: "" };

const normalizeAttachment = (document) => {
	if (!document) return DEFAULT_ATTACHMENT;
	const type = String(document.attachment_type || "").toLowerCase();
	if (type === "file") {
		const value =
			document.path ||
			document.attachment ||
			(document.name ? `Documents/${document.name}` : "");
		return { type, value };
	}
	if (type === "url") {
		const value = document.path || document.attachment || document.url || "";
		return { type, value };
	}
	return DEFAULT_ATTACHMENT;
};

const getAssignedMachines = (document) => {
	if (!document) return [];
	if (
		Array.isArray(document.assigned_machines) &&
		document.assigned_machines.every((machine) => typeof machine === "object")
	) {
		return document.assigned_machines;
	}
	if (Array.isArray(document.machine_details)) {
		return document.machine_details;
	}
	return [];
};

const getCategories = (document) => {
	if (!document) return [];
	if (Array.isArray(document.categories)) return document.categories;
	return [];
};

const getDocumentFileName = (attachment, fallbackName = "") => {
	const raw = String(attachment || "");
	return (
		raw.split("/").pop() ||
		String(fallbackName || "").split("/").pop() ||
		"document"
	);
};

const getFileExtension = (value = "") => {
	const fileName = getDocumentFileName(value);
	const lastDot = fileName.lastIndexOf(".");
	return lastDot >= 0 ? fileName.slice(lastDot + 1).toLowerCase() : "";
};

const getColumnLabel = (index) => {
	let value = Number(index) + 1;
	let label = "";
	while (value > 0) {
		const remainder = (value - 1) % 26;
		label = String.fromCharCode(65 + remainder) + label;
		value = Math.floor((value - 1) / 26);
	}
	return label;
};

const getPreviewType = (attachment) => {
	if (attachment.type === "url") return "url";
	if (attachment.type !== "file") return "none";

	const ext = getFileExtension(attachment.value);
	if (ext === "pdf") return "pdf";
	if (["png", "jpg", "jpeg", "gif", "bmp", "webp", "svg"].includes(ext)) return "image";
	if (["txt", "csv", "json", "xml", "md"].includes(ext)) return "text";
	if (["xls", "xlsx"].includes(ext)) return "spreadsheet";
	if (ext === "docx") return "docx";
	return "unsupported";
};

const renderMachineLabel = (machine = {}) =>
	machine?.machine_name || machine?.machine_number || machine?.machine_id || "";

const DocumentViewModal = ({
	open,
	setOpen,
	document: documentData,
	onClose,
	onEdit,
	onDelete,
	width = "80vw",
}) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const [zoomLevel, setZoomLevel] = useState(1);
	const [pageCount, setPageCount] = useState(0);
	const [isFullscreen, setIsFullscreen] = useState(false);
	const [previewState, setPreviewState] = useState({
		loading: false,
		error: "",
		signedUrl: "",
		content: null,
	});

	const attachment = useMemo(() => normalizeAttachment(documentData), [documentData]);
	const previewType = useMemo(() => getPreviewType(attachment), [attachment]);
	const assignedMachines = useMemo(() => getAssignedMachines(documentData), [documentData]);
	const categories = useMemo(() => getCategories(documentData), [documentData]);

	const getSignedDocumentUrl = useCallback(async (mode = "view") => {
		if (!documentData) return "";
		try {
			const filename = getDocumentFileName(attachment.value, documentData.name);
			const response = await getDocumentAccessUrl(encodeURIComponent(filename), mode);
			return response?.data?.url || "";
		} catch (error) {
			console.error("Unable to get document access url", error);
			return "";
		}
	}, [attachment.value, documentData]);

	useEffect(() => {
		if (!open) return;
		setZoomLevel(1);
		setPageCount(0);
	}, [open, documentData?.id]);

	useEffect(() => {
		if (!open) {
			setIsFullscreen(false);
		}
	}, [open]);

	useEffect(() => {
		let active = true;

		const loadPreview = async () => {
			if (!open || !documentData) return;

			if (attachment.type === "url") {
				setPreviewState({
					loading: false,
					error: "",
					signedUrl: "",
					content: attachment.value || "",
				});
				return;
			}

			if (attachment.type !== "file" || !attachment.value) {
				setPreviewState({
					loading: false,
					error: "",
					signedUrl: "",
					content: null,
				});
				return;
			}

			setPreviewState({
				loading: true,
				error: "",
				signedUrl: "",
				content: null,
			});

			try {
				const signedUrl = await getSignedDocumentUrl("view");
				if (!signedUrl) {
					throw new Error(
						t("ALERT_MESSAGES.UNABLE_TO_OPEN_DOCUMENT") ||
						"Unable to open the document. Please try again later."
					);
				}

				let content = null;
				if (previewType === "text") {
					const response = await fetch(signedUrl);
					content = await response.text();
				} else if (previewType === "spreadsheet") {
					const response = await fetch(signedUrl);
					const arrayBuffer = await response.arrayBuffer();
					const workbook = XLSX.read(arrayBuffer, { type: "array" });
					const firstSheetName = workbook.SheetNames[0];
					const firstSheet = workbook.Sheets[firstSheetName];
					const range = XLSX.utils.decode_range(firstSheet["!ref"] || "A1:A1");
					const rows = XLSX.utils.sheet_to_json(firstSheet, {
						header: 1,
						defval: "",
						blankrows: true,
					});
					content = {
						sheetName: firstSheetName,
						rows,
						columnCount: Math.max(range.e.c + 1, ...rows.map((row) => row.length), 1),
						rowCount: Math.max(range.e.r + 1, rows.length, 1),
					};
				} else if (previewType === "docx") {
					const response = await fetch(signedUrl);
					const arrayBuffer = await response.arrayBuffer();
					const result = await mammoth.convertToHtml({ arrayBuffer });
					content = result?.value || "";
				}

				if (!active) return;
				setPreviewState({
					loading: false,
					error: "",
					signedUrl,
					content,
				});
			} catch (error) {
				if (!active) return;
				setPreviewState({
					loading: false,
					error:
						error?.message ||
						(t("ALERT_MESSAGES.UNABLE_TO_OPEN_DOCUMENT") ||
							"Unable to open the document. Please try again later."),
					signedUrl: "",
					content: null,
				});
			}
		};

		loadPreview();
		return () => {
			active = false;
		};
	}, [attachment.type, attachment.value, documentData, getSignedDocumentUrl, open, previewType, t]);

	const handleClose = () => {
		if (onClose) onClose();
	};

	const handleDownload = async () => {
		if (!documentData) return;
		try {
			const filename = getDocumentFileName(attachment.value, documentData.name);
			const signedUrl = await getSignedDocumentUrl("download");
			if (!signedUrl) throw new Error("Missing signed download url");
			const link = window.document.createElement("a");
			link.href = signedUrl;
			link.download = filename;
			window.document.body.appendChild(link);
			link.click();
			window.document.body.removeChild(link);
		} catch (_error) {
			Swal.fire({
				icon: "error",
				title: t("ALERT_MESSAGES.ERROR"),
				text: t("ALERT_MESSAGES.FAILED_TO_DOWNLOAD_FILE_PLEASE_TRY_AGAING"),
			});
		}
	};

	const handleOpenLink = () => {
		if (attachment.type === "url" && attachment.value) {
			window.open(attachment.value, "_blank", "noopener,noreferrer");
		}
	};

	const renderLoadingState = () => (
		<Box sx={{ p: 2 }}>
			<Skeleton variant="text" width="30%" height={36} />
			<Skeleton variant="rounded" height={52} sx={{ mt: 1.5, mb: 2 }} />
			<Skeleton variant="rounded" height={420} />
		</Box>
	);

	const renderPreviewContent = () => {
		if (previewState.loading) return renderLoadingState();

		if (previewState.error) {
			return <Alert severity="error">{previewState.error}</Alert>;
		}

		if (attachment.type === "url") {
			return (
				<Alert severity="info">
					<Typography variant="body2">
						{attachment.value || (t("UI_TEXT.NO_ATTACHMENTS") || "No attachment")}
					</Typography>
				</Alert>
			);
		}

		if (attachment.type !== "file" || !attachment.value) {
			return (
				<Alert severity="info">
					{t("UI_TEXT.NO_ATTACHMENTS") || "No attachment"}
				</Alert>
			);
		}

		if (previewType === "pdf" && previewState.signedUrl) {
			return (
				<Box sx={{ display: "flex", justifyContent: "center" }}>
					<PdfDocument
						file={previewState.signedUrl}
						loading={<CircularProgress />}
						onLoadSuccess={({ numPages }) => setPageCount(numPages)}
					>
						{Array.from({ length: pageCount || 1 }).map((_, index) => (
							<Box key={`page-${index + 1}`} sx={{ mb: 2 }}>
								<PdfPage
									pageNumber={index + 1}
									scale={zoomLevel}
									renderTextLayer
									renderAnnotationLayer
								/>
							</Box>
						))}
					</PdfDocument>
				</Box>
			);
		}

		if (previewType === "image" && previewState.signedUrl) {
			return (
				<Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
					<Box
						component="img"
						src={previewState.signedUrl}
						alt={documentData?.name || "Document preview"}
						sx={{
							maxWidth: `${Math.max(40, zoomLevel * 100)}%`,
							borderRadius: 2,
							boxShadow: theme.shadows[2],
						}}
					/>
				</Box>
			);
		}

		if (previewType === "text") {
			return (
				<Box
					sx={{
						p: 2,
						borderRadius: 2,
						border: `1px solid ${theme.palette.divider}`,
						bgcolor: theme.palette.background.default,
						fontFamily: "monospace",
						fontSize: `${Math.max(0.8, zoomLevel)}rem`,
						whiteSpace: "pre-wrap",
						wordBreak: "break-word",
					}}
				>
					{previewState.content || ""}
				</Box>
			);
		}

		if (previewType === "spreadsheet") {
			const sheetData = previewState.content || {};
			const rows = Array.isArray(sheetData.rows) ? sheetData.rows : [];
			const columnCount = Number(sheetData.columnCount || 1);
			const rowCount = Number(sheetData.rowCount || rows.length || 1);
			const normalizedRows = Array.from({ length: rowCount }, (_, rowIndex) => {
				const row = Array.isArray(rows[rowIndex]) ? rows[rowIndex] : [];
				return Array.from({ length: columnCount }, (_, colIndex) => row[colIndex] ?? "");
			});
			return (
				<Box>
					{sheetData.sheetName ? (
						<Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
							Sheet: {sheetData.sheetName}
						</Typography>
					) : null}
					<Box
						sx={{
							overflow: "auto",
							border: `1px solid ${theme.palette.divider}`,
							borderRadius: 2,
							bgcolor: theme.palette.background.paper,
						}}
					>
						<Box
							sx={{
								transform: `scale(${zoomLevel})`,
								transformOrigin: "top left",
								width: `${100 / zoomLevel}%`,
							}}
						>
							<table
								style={{
									width: "100%",
									borderCollapse: "collapse",
									tableLayout: "fixed",
									background: theme.palette.background.paper,
								}}
							>
								<thead>
									<tr>
										<th
											style={{
												position: "sticky",
												top: 0,
												left: 0,
												zIndex: 3,
												width: "58px",
												minWidth: "58px",
												background: theme.palette.grey[100],
												border: `1px solid ${theme.palette.divider}`,
												padding: "10px 8px",
											}}
										/>
										{Array.from({ length: columnCount }, (_, columnIndex) => (
											<th
												key={`col-${columnIndex}`}
												style={{
													position: "sticky",
													top: 0,
													zIndex: 2,
													background: theme.palette.grey[100],
													border: `1px solid ${theme.palette.divider}`,
													padding: "10px 8px",
													fontWeight: 700,
													textAlign: "center",
													minWidth: "140px",
												}}
											>
												{getColumnLabel(columnIndex)}
											</th>
										))}
									</tr>
								</thead>
								<tbody>
									{normalizedRows.map((row, rowIndex) => (
										<tr key={`row-${rowIndex}`}>
											<th
												style={{
													position: "sticky",
													left: 0,
													zIndex: 1,
													background: theme.palette.grey[100],
													border: `1px solid ${theme.palette.divider}`,
													padding: "10px 8px",
													fontWeight: 700,
													textAlign: "center",
													width: "58px",
													minWidth: "58px",
												}}
											>
												{rowIndex + 1}
											</th>
											{row.map((cell, cellIndex) => (
												<td
													key={`cell-${rowIndex}-${cellIndex}`}
													style={{
														border: `1px solid ${theme.palette.divider}`,
														padding: "10px 12px",
														fontSize: "0.95rem",
														verticalAlign: "top",
														minWidth: "140px",
														whiteSpace: "pre-wrap",
														wordBreak: "break-word",
													}}
												>
													{String(cell ?? "")}
												</td>
											))}
										</tr>
									))}
								</tbody>
							</table>
						</Box>
					</Box>
				</Box>
			);
		}

		if (previewType === "docx") {
			return (
				<Box
					sx={{
						overflow: "auto",
						border: `1px solid ${theme.palette.divider}`,
						borderRadius: 2,
						bgcolor: theme.palette.grey[100],
						p: 2.5,
					}}
				>
					<Box
						sx={{
							transform: `scale(${zoomLevel})`,
							transformOrigin: "top center",
							width: `${100 / zoomLevel}%`,
							maxWidth: "860px",
							mx: "auto",
							backgroundColor: theme.palette.background.paper,
							borderRadius: 1,
							boxShadow: theme.shadows[3],
							p: { xs: 2.5, md: "40px 52px" },
							lineHeight: 1.6,
							color: theme.palette.text.primary,
							wordBreak: "break-word",
							"& p": { margin: "0 0 0.9em 0" },
							"& h1, & h2, & h3, & h4, & h5, & h6": {
								margin: "0.8em 0 0.45em 0",
								lineHeight: 1.3,
							},
							"& img": { maxWidth: "100%", height: "auto" },
							"& table": { borderCollapse: "collapse", width: "100%", margin: "0.8em 0" },
							"& th, & td": {
								border: `1px solid ${theme.palette.divider}`,
								padding: "6px 8px",
							},
						}}
						dangerouslySetInnerHTML={{
							__html: previewState.content || "<p>No preview available.</p>",
						}}
					/>
				</Box>
			);
		}

		if (previewState.signedUrl) {
			return (
				<Alert severity="info">
					<Typography variant="body2">
						{t("ALERT_MESSAGES.PREVIEW_NOT_SUPPORTED") || "Preview is not available for this file type."}
					</Typography>
				</Alert>
			);
		}

		return null;
	};

	const descriptionText = documentData?.description
		? String(documentData.description).replace(/<[^>]+>/g, "").trim()
		: "";
	const visibilityStatus = documentData?.visibility_status || "";

	const content = (
		<Box>
			<Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 2.5 }}>
				{/* Description */}
				<Box>
					<Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75, fontWeight: 500 }}>
						{t("TABLE_HEADERS.DESCRIPTION") || "Description"}
					</Typography>
					<Box
						sx={{
							p: 1.5,
							borderRadius: 1,
							border: `1px solid ${theme.palette.divider}`,
							bgcolor: theme.palette.action.hover,
							minHeight: 56,
						}}
					>
						<Typography variant="body2" color={descriptionText ? "text.primary" : "text.secondary"}>
							{descriptionText || t("UI_TEXT.NO_DESCRIPTION_AVAILABLE_1") || "No Description available"}
						</Typography>
					</Box>
				</Box>

				{/* Assigned to Machines*/}
				<Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
					<Box sx={{ flex: 1, minWidth: 0 }}>
						<Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75, fontWeight: 500 }}>
							{t("TABLE_HEADERS.ASSIGNED_MACHINE") || "Assigned to Machines"}
						</Typography>
						{assignedMachines.length > 0 ? (
							<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
								{assignedMachines.map((machine, index) => (
									<Chip
										key={`${renderMachineLabel(machine)}-${index}`}
										size="small"
										variant="outlined"
										label={renderMachineLabel(machine)}
									/>
								))}
							</Box>
						) : (
							<Typography variant="body2" color="text.secondary">
								{t("UI_TEXT.NO_MACHINES_ASSIGNED_1") || "No machine(s) assigned"}
							</Typography>
						)}
					</Box>

					{visibilityStatus && (
						<Box sx={{ flex: "0 0 auto" }}>
							<Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75, fontWeight: 500 }}>
								{t("TABLE_HEADERS.STATUS") || "Status"}
							</Typography>
							<Chip
								size="small"
								label={visibilityStatus.charAt(0).toUpperCase() + visibilityStatus.slice(1)}
								color={visibilityStatus === "public" ? "success" : "default"}
								variant="outlined"
							/>
						</Box>
					)}
				</Box>
			</Box>

			<Divider sx={{ mb: 2 }} />
			<Box>{renderPreviewContent()}</Box>
		</Box>
	);

	const actionButtons = (
		<>
			<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", gap: 1, flexWrap: "wrap" }}>
				<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
					<Button variant="outlined" onClick={handleClose}>
						{t("BUTTON.CLOSE")}
					</Button>
					{onDelete && documentData ? (
						<Button
							variant="contained"
							color="error"
							startIcon={<DeleteIcon />}
							onClick={() => onDelete(documentData)}
						>
							{t("BUTTON.DELETE")}
						</Button>
					) : null}
				</Stack>
				<Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
					<Tooltip title={t("UI_TEXT.TOOLTIP_ZOOM_OUT") || "Zoom Out"}>
						<span>
							<IconButton
								size="small"
								onClick={() => setZoomLevel((prev) => Math.max(0.6, Number((prev - 0.1).toFixed(2))))}
								disabled={previewState.loading || attachment.type === "url" || attachment.type === "none"}
							>
								<ZoomOutIcon fontSize="small" />
							</IconButton>
						</span>
					</Tooltip>
					<Typography variant="caption" sx={{ minWidth: 42, textAlign: "center" }}>
						{Math.round(zoomLevel * 100)}%
					</Typography>
					<Tooltip title={t("UI_TEXT.TOOLTIP_ZOOM_IN") || "Zoom In"}>
						<span>
							<IconButton
								size="small"
								onClick={() => setZoomLevel((prev) => Math.min(2.5, Number((prev + 0.1).toFixed(2))))}
								disabled={previewState.loading || attachment.type === "url" || attachment.type === "none"}
							>
								<ZoomInIcon fontSize="small" />
							</IconButton>
						</span>
					</Tooltip>
					<Tooltip title={t("UI_TEXT.TOOLTIP_RESET_ZOOM") || "Reset Zoom"}>
						<IconButton size="small" onClick={() => setZoomLevel(1)}>
							<RestartAltIcon fontSize="small" />
						</IconButton>
					</Tooltip>
					<Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
					{attachment.type === "url" && attachment.value ? (
						<Button
							startIcon={<LinkOutlined />}
							variant="contained"
							color="primary"
							onClick={handleOpenLink}
						>
							{t("BUTTON.OPEN_LINK") || "Open Link"}
						</Button>
					) : null}
					{attachment.type === "file" && attachment.value ? (
						<Button
							variant="contained"
							color="primary"
							startIcon={<DownloadIcon />}
							onClick={handleDownload}
						>
							{t("UI_TEXT.DOWNLOAD") || "Download"}
						</Button>
					) : null}
					{onEdit && documentData ? (
						<Button
							variant="contained"
							color="primary"
							startIcon={<EditIcon />}
							onClick={() => onEdit(documentData)}
						>
							{t("BUTTON.EDIT")}
						</Button>
					) : null}
				</Stack>
			</Box>
		</>
	);

	const headerActions = (
		<Tooltip title={isFullscreen ? "Exit Fullscreen" : "Open Fullscreen"}>
			<IconButton onClick={() => setIsFullscreen((prev) => !prev)}>
				{isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
			</IconButton>
		</Tooltip>
	);

	return (
		<ModalWrapper
			open={open}
			setOpen={setOpen}
			onClose={handleClose}
			title={documentData?.name || (t("TABLE_HEADERS.DOCUMENT_NAME") || "Document")}
			subtitle={categories.map((category) => category?.category_name).filter(Boolean).join(" | ")}
			content={content}
			actions={actionButtons}
			width={width}
			headerActions={headerActions}
			fullscreen={isFullscreen}
		/>
	);
};

export default DocumentViewModal;
